/**
 * A tiny "markdown-ish" parser for assistant replies: paragraphs, bullet or
 * numbered lists, **bold**, headings (shown bold) and validated
 * [[nav:KEY|Label]] links. It produces plain data that AssistantReply.tsx
 * renders as React elements, so model output is never injected as HTML.
 */
import { isNavTarget, type NavTargetKey } from './navTargets';

export type InlineToken =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'nav'; target: NavTargetKey; label: string };

export type ReplyBlock =
  | { kind: 'paragraph'; lines: InlineToken[][] }
  | { kind: 'list'; ordered: boolean; items: InlineToken[][] };

const INLINE_RE = /\*\*(.+?)\*\*|__(.+?)__|\[\[nav:([^|\]]+)\|([^\]]+)\]\]|`([^`]+)`/g;
const BULLET_RE = /^\s*[-*•]\s+(.*)$/;
const NUMBERED_RE = /^\s*\d+[.)]\s+(.*)$/;
const HEADING_RE = /^\s*#{1,6}\s+(.*)$/;

function pushText(tokens: InlineToken[], text: string): void {
  if (!text) return;
  const last = tokens[tokens.length - 1];
  if (last && last.kind === 'text') {
    tokens[tokens.length - 1] = { kind: 'text', text: last.text + text };
  } else {
    tokens.push({ kind: 'text', text });
  }
}

export function tokenizeInline(line: string): InlineToken[] {
  const tokens: InlineToken[] = [];
  let cursor = 0;
  for (const match of line.matchAll(INLINE_RE)) {
    const index = match.index ?? 0;
    pushText(tokens, line.slice(cursor, index));
    const [, bold, underscoreBold, navKey, navLabel, code] = match;
    if (bold !== undefined || underscoreBold !== undefined) {
      tokens.push({ kind: 'bold', text: (bold ?? underscoreBold) as string });
    } else if (navKey !== undefined && navLabel !== undefined) {
      const key = navKey.trim();
      if (isNavTarget(key)) tokens.push({ kind: 'nav', target: key, label: navLabel.trim() });
      else pushText(tokens, navLabel.trim());
    } else if (code !== undefined) {
      pushText(tokens, code);
    }
    cursor = index + match[0].length;
  }
  pushText(tokens, line.slice(cursor));
  return tokens;
}

function listItem(line: string): { ordered: boolean; text: string } | null {
  const numbered = NUMBERED_RE.exec(line);
  if (numbered) return { ordered: true, text: numbered[1] ?? '' };
  const bullet = BULLET_RE.exec(line);
  if (bullet) return { ordered: false, text: bullet[1] ?? '' };
  return null;
}

export function parseReply(reply: string): ReplyBlock[] {
  const blocks: ReplyBlock[] = [];
  let current: ReplyBlock | null = null;
  const flush = () => {
    if (current) blocks.push(current);
    current = null;
  };

  for (const raw of reply.replace(/\r\n/g, '\n').split('\n')) {
    if (!raw.trim()) {
      flush();
      continue;
    }
    const item = listItem(raw);
    if (item) {
      if (!current || current.kind !== 'list') {
        flush();
        current = { kind: 'list', ordered: item.ordered, items: [] };
      }
      current.items.push(tokenizeInline(item.text.trim()));
      continue;
    }
    if (!current || current.kind !== 'paragraph') {
      flush();
      current = { kind: 'paragraph', lines: [] };
    }
    const heading = HEADING_RE.exec(raw);
    current.lines.push(heading ? [{ kind: 'bold', text: (heading[1] ?? '').trim() }] : tokenizeInline(raw.trim()));
  }
  flush();
  return blocks;
}
